import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Funnel, MoreVertical, Pencil, Rocket } from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
	getCreatedJobs,
	getPublishJobsByUID,
} from "../Services/DraftJobService";

import ColumnFilterModal from "../../../CommonModals/ColumnFilterModal";
import Pagination from "../../../CommonModals/Pagination";
import ConfirmationAlert from "../../../../Alerts/ConfirmationAlert";

const DraftJobTable = () => {
	const navigate = useNavigate();
	const menuRef = useRef(null);

	//  DATA STATE
	const [jobs, setJobs] = useState([]);

	//  UI STATE
	const [loading, setLoading] = useState(true);
	const [openMenu, setOpenMenu] = useState(null);
	const [confirmAlert, setConfirmAlert] = useState(null);

	//  SEARCH & FILTER
	const [searchTerm, setSearchTerm] = useState("");
	const [activeFilter, setActiveFilter] = useState(null);
	const [filterSearch, setFilterSearch] = useState("");

	const [filters, setFilters] = useState({
		roleCode: [],
		jobTitle: [],
		department: [],
		location: [],
		grade: [],
		function: [],
	});

	//  PAGINATION
	const [currentPage, setCurrentPage] = useState(1);
	const [jobsPerPage, setJobsPerPage] = useState(10);

	//  FETCH JOBS
	useEffect(() => {
		const fetchJobs = async () => {
			try {
				const response = await getCreatedJobs();
				const createdJobs = response?.data?.createdJobs;

				setJobs(Array.isArray(createdJobs) ? createdJobs : []);
			} catch (error) {
				setJobs([]);
			} finally {
				setLoading(false);
			}
		};

		fetchJobs();
	}, []);

	//  FILTER OPTIONS
	const filterOptions = {
		roleCode: [...new Set(jobs.map((j) => j.roleCode))],
		jobTitle: [...new Set(jobs.map((j) => j.jobTitle))],
		department: [...new Set(jobs.map((j) => j.department))],
		location: [...new Set(jobs.map((j) => j.location))],
		grade: [...new Set(jobs.map((j) => j.grade))],
		function: [...new Set(jobs.map((j) => j.function))],
	};

	//  FILTERED DATA
	const filteredJobs = jobs.filter((job) => {
		const matchesFilters =
			(!filters.roleCode.length || filters.roleCode.includes(job.roleCode)) &&
			(!filters.jobTitle.length || filters.jobTitle.includes(job.jobTitle)) &&
			(!filters.department.length ||
				filters.department.includes(job.department)) &&
			(!filters.location.length || filters.location.includes(job.location)) &&
			(!filters.grade.length || filters.grade.includes(job.grade)) &&
			(!filters.function.length || filters.function.includes(job.function));

		const matchesSearch = [
			job.roleCode,
			job.jobTitle,
			job.department,
			job.location,
			job.grade,
			job.function,
		].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

		return matchesFilters && matchesSearch;
	});

	//  PAGINATION LOGIC
	const indexOfLast = currentPage * jobsPerPage;
	const indexOfFirst = indexOfLast - jobsPerPage;
	const currentJobs = filteredJobs.slice(indexOfFirst, indexOfLast);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm, filters]);

	//  OUTSIDE CLICK
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest(".action-menu")) {
				setOpenMenu(null);
			}
		};

		document.addEventListener("click", handleClickOutside);

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, []);

	//  HANDLERS
	const handlePublishJob = async (jobUID) => {
		try {
			const res = await getPublishJobsByUID(jobUID);

			if (res?.data?.status === "Success") {
				navigate("/Dashboard/Live-Jobs");
			}
		} catch (error) {
			console.error("Publish error:", error);
		}
	};

	const confirmPublish = (jobUID) => {
		setConfirmAlert({
			message: "Are you sure you want to publish this job?",
			onConfirm: () => handlePublishJob(jobUID),
		});
	};

	return (
		<>
			{/*  MAIN CONTAINER  */}
			<div className="w-full py-8 mt-12">
				<div className="max-w-[1240px] mx-auto">
					<div className="bg-white rounded-2xl px-6 py-6 shadow-sm">
						{/*  HEADER  */}
						<div className="bg-master-header text-white rounded-xl px-3 py-2 mb-6">
							<h3 className="font-bold text-lg">Draft Jobs</h3>
						</div>

						{/*  SEARCH  */}
						<div className="mb-6 flex justify-between items-center flex-wrap gap-4">
							<input
								type="text"
								placeholder="Search jobs..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
							/>

							<button
								onClick={() => navigate("/Dashboard/Post-Job")}
								className="bg-primary text-white rounded-full px-6 py-2 font-semibold"
							>
								+ Create Job
							</button>
						</div>

						{/*  TABLE  */}
						<div className="border rounded-lg overflow-visible">
							<table className="w-full text-center">
								<thead className="bg-[#f5f8ff] text-sm uppercase">
									<tr>
										<th className="py-3">#</th>
										<th className="py-3">Role Code</th>
										<th className="py-3">job Title</th>

										{["function", "department", "location", "grade"].map(
											(col) => (
												<th key={col} className="py-3">
													<div className="flex justify-center items-center gap-2 capitalize">
														{col}
														<Funnel
															size={14}
															onClick={() => setActiveFilter(col)}
															className="cursor-pointer text-primary"
														/>
													</div>
												</th>
											),
										)}

										<th className="py-3">Action</th>
									</tr>
								</thead>

								<tbody>
									{loading ? (
										<SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
											{[...Array(5)].map((_, rowIdx) => (
												<tr key={rowIdx} className="border-t">
													{[...Array(7)].map((__, colIdx) => (
														<td key={colIdx} className="py-4 px-3">
															<Skeleton height={20} />
														</td>
													))}
												</tr>
											))}
										</SkeletonTheme>
									) : currentJobs.length ? (
										currentJobs.map((job, idx) => (
											<tr key={job.jobUID} className="border-t">
												<td className="py-3">{indexOfFirst + idx + 1}</td>

												<td
													className="text-blue-600 font-semibold cursor-pointer"
													onClick={() =>
														navigate(
															`/Dashboard/View-Job-Details/${job.jobUID}`,
														)
													}
												>
													{job.roleCode || "Role Code Missing"}
												</td>

												<td>{job.jobTitle}</td>
												<td>{job.function}</td>
												<td>{job.department}</td>
												<td>{job.location}</td>
												<td>{job.grade}</td>

												<td className="px-3 text-xl">
													<div className="relative inline-block action-menu">
														<button
															onClick={() =>
																setOpenMenu(
																	openMenu === job.jobUID ? null : job.jobUID,
																)
															}
															className="p-1 hover:bg-gray-100 rounded-full"
														>
															<MoreVertical size={20} />
														</button>

														{openMenu === job.jobUID && (
															<div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg z-50">
																<button
																	className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
																	onClick={() =>
																		navigate(
																			`/Dashboard/Update-Job/${job.jobUID}`,
																		)
																	}
																>
																	<Pencil size={16} />
																	Update Job
																</button>

																<button
																	className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
																	onClick={() => confirmPublish(job.jobUID)}
																>
																	<Rocket size={16} />
																	Publish Job
																</button>
															</div>
														)}
													</div>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="7" className="py-6 text-red-500 text-center">
												No Draft Job Found.
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						{/*  PAGINATION  */}
						<Pagination
							totalItems={filteredJobs.length}
							itemsPerPage={jobsPerPage}
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							setItemsPerPage={setJobsPerPage}
						/>
					</div>
				</div>
			</div>

			{/*  FILTER MODAL  */}
			<ColumnFilterModal
				show={!!activeFilter}
				onHide={() => setActiveFilter(null)}
				title={activeFilter}
				items={filterOptions[activeFilter] || []}
				filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
					item?.toLowerCase().includes(filterSearch.toLowerCase()),
				)}
				setFilteredItems={() => {}}
				selectedItems={filters[activeFilter] || []}
				setSelectedItems={(values) =>
					setFilters((prev) => ({
						...prev,
						[activeFilter]: values,
					}))
				}
				searchText={filterSearch}
				setSearchText={setFilterSearch}
			/>

			{/*  CONFIRMATION ALERT  */}
			{confirmAlert && (
				<ConfirmationAlert
					title="Warning"
					message={confirmAlert.message}
					onClose={() => setConfirmAlert(null)}
					onConfirm={() => {
						confirmAlert.onConfirm();
						setConfirmAlert(null);
					}}
					onCancel={() => setConfirmAlert(null)}
				/>
			)}
		</>
	);
};

export default DraftJobTable;
